import { useState, useEffect } from 'react';
import { Phone, Globe, ExternalLink, ChevronDown, ChevronUp, ArrowLeft, Anchor } from 'lucide-react';
import { motion } from 'framer-motion';
import { getIslandWineries } from '../lib/api';

const FERRY_DATA = {
  'salt-spring-island': {
    island: 'Salt Spring Island',
    tagline: 'A scenic ferry crossing to Salt Spring Island — budget approximately $55–65 return per vehicle.',
    routes: [
      {
        label: 'From Victoria / Saanich area',
        crossing: 'Swartz Bay → Fulford Harbour',
        duration: '35 min crossing',
        schedule: 'Sailings approximately every 1–2 hours',
        cost: '~$47–55 car + driver, one way',
        walkOn: '~$17 walk-on, one way',
      },
      {
        label: 'From Cowichan / Duncan area',
        crossing: 'Crofton → Vesuvius Bay',
        duration: '20 min crossing',
        schedule: 'Sailings approximately every 1–2 hours',
        cost: '~$27–32 car + driver, one way',
        walkOn: '~$14 walk-on, one way',
      },
    ],
    ferryNote: 'First come, first served on most sailings. Arrive 30–45 minutes early in summer.',
    bcFerriesUrl: 'https://www.bcferries.com/routes-fares/schedules',
  },
  'pender-island': {
    island: 'Pender Island',
    tagline: 'A relaxed 45-minute ferry to one of the Southern Gulf Islands\' hidden wine gems.',
    routes: [
      {
        label: 'From Victoria / Swartz Bay',
        crossing: 'Swartz Bay → Otter Bay (North Pender)',
        duration: '~45 min crossing',
        schedule: 'Sailings approximately every 1–2 hours',
        cost: '~$47–55 car + driver, one way',
        walkOn: '~$17 walk-on, one way',
      },
    ],
    ferryNote: 'First come, first served. Arrive early in summer — sailings can fill up on weekends.',
    bcFerriesUrl: 'https://www.bcferries.com/routes-fares/schedules',
  },
  'saturna-island': {
    island: 'Saturna Island',
    tagline: 'The most remote of the Gulf Islands — well worth the journey for a truly unhurried winery visit.',
    routes: [
      {
        label: 'From Victoria / Swartz Bay',
        crossing: 'Swartz Bay → Saturna Island',
        duration: '~1.5–2 hours (may stop at other islands)',
        schedule: 'Sailings approximately every 2–4 hours — check schedule carefully',
        cost: '~$47–55 car + driver, one way',
        walkOn: '~$17 walk-on, one way',
      },
    ],
    ferryNote: 'Saturna has limited sailings. Plan your day carefully and confirm the schedule before you go — missing the last sailing means staying overnight.',
    bcFerriesUrl: 'https://www.bcferries.com/routes-fares/schedules',
  },
  'denman-island': {
    island: 'Denman Island',
    tagline: 'A quick 10-minute hop from the Island Highway to a quieter pace.',
    routes: [
      {
        label: 'From Courtenay / Comox area',
        crossing: 'Buckley Bay → Denman Island',
        duration: '10 min crossing',
        schedule: 'Sailings approximately every 1 hour',
        cost: '~$17–20 car + driver, one way',
        walkOn: '~$9 walk-on, one way',
      },
    ],
    ferryNote: 'One of BC Ferries\' shortest crossings. First come, first served — rarely a long wait.',
    bcFerriesUrl: 'https://www.bcferries.com/routes-fares/schedules',
  },
  'hornby-island': {
    island: 'Hornby Island',
    tagline: 'Two short ferry hops to one of BC\'s most beloved islands.',
    routes: [
      {
        label: 'Ferry 1 — from Courtenay / Comox area',
        crossing: 'Buckley Bay → Denman Island',
        duration: '10 min crossing',
        schedule: 'Sailings approximately every 1 hour',
        cost: '~$17–20 car + driver, one way',
        walkOn: '~$9 walk-on, one way',
      },
      {
        label: 'Ferry 2 — from Denman Island',
        crossing: 'Gravelly Bay (Denman) → Shingle Spit (Hornby)',
        duration: '10 min crossing',
        schedule: 'Sailings approximately every 1 hour',
        cost: '~$17–20 car + driver, one way',
        walkOn: '~$9 walk-on, one way',
      },
    ],
    ferryNote: 'Two ferry crossings required. Budget an extra 45–60 minutes for both ferries and the short drive across Denman Island between them.',
    bcFerriesUrl: 'https://www.bcferries.com/routes-fares/schedules',
  },
};

function WineryCard({ winery }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden"
    >
      {winery.image_url && (
        <img src={winery.image_url} alt={winery.name} className="w-full h-44 object-cover" />
      )}
      <div className="p-4">
        <h3 className="text-base font-bold text-stone-900">{winery.name}</h3>
        {winery.address && <p className="text-sm text-stone-500 mt-0.5">{winery.address}</p>}

        {(winery.revised_description || winery.description) && (
          <p className="text-sm text-stone-600 mt-2 leading-relaxed">
            {winery.revised_description || winery.description}
          </p>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          {winery.tasting_fee && (
            <span className="text-xs bg-stone-100 text-stone-600 px-2 py-1 rounded-full">
              Tasting: {winery.tasting_fee}
            </span>
          )}
          {winery.reservation_required && (
            <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1 rounded-full">
              Reservation required
            </span>
          )}
        </div>

        <button
          onClick={() => setExpanded(e => !e)}
          className="mt-3 w-full flex items-center justify-between text-sm text-[#722F37] font-medium py-1"
        >
          <span>{expanded ? 'Less detail' : 'More detail'}</span>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {expanded && (
          <div className="mt-2 space-y-3 text-sm border-t border-stone-100 pt-3">
            {winery.hours_text && (
              <div>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Hours</p>
                <p className="text-stone-700">{winery.hours_text}</p>
              </div>
            )}
            {winery.wines_offered?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Wines</p>
                <p className="text-stone-700">{winery.wines_offered.join(', ')}</p>
              </div>
            )}
            {winery.features?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Features</p>
                <p className="text-stone-700">{winery.features.join(', ')}</p>
              </div>
            )}
            {winery.vineyard_experiences?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Experiences</p>
                <p className="text-stone-700">{winery.vineyard_experiences.join(', ')}</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-4 flex gap-2">
          {winery.phone && (
            <a
              href={`tel:${winery.phone}`}
              className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-[#722F37] text-white text-sm font-medium rounded-lg hover:bg-[#5a252c] transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call Winery
            </a>
          )}
          {winery.website && (
            <a
              href={winery.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 py-2 px-3 border border-stone-300 text-stone-600 text-sm font-medium rounded-lg hover:bg-stone-50 transition-colors"
            >
              <Globe className="w-4 h-4" />
              Website
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function FerryIslandView({ region, onBack }) {
  const [wineries, setWineries] = useState([]);
  const [loading, setLoading] = useState(true);

  const ferry = FERRY_DATA[region];

  useEffect(() => {
    getIslandWineries(region)
      .then(setWineries)
      .catch(() => setWineries([]))
      .finally(() => setLoading(false));
  }, [region]);

  if (!ferry) return null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="w-10 h-10 rounded-full bg-stone-200 hover:bg-stone-300 flex items-center justify-center text-stone-600 transition-colors"
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-lg font-bold text-stone-900">{ferry.island}</h2>
          <p className="text-sm text-stone-500">Island wineries &amp; ferry info</p>
        </div>
      </div>

      {/* Ferry Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 space-y-3">
        <div className="flex items-start gap-2">
          <Anchor className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-semibold text-blue-900">{ferry.tagline}</p>
        </div>

        {ferry.routes.map((route, i) => (
          <div key={i} className={i > 0 ? 'border-t border-blue-200 pt-3' : ''}>
            {ferry.routes.length > 1 && (
              <p className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-1.5">{route.label}</p>
            )}
            <div className="space-y-0.5 text-sm text-blue-900">
              <p className="font-semibold">{route.crossing}</p>
              <p>{route.duration} &middot; {route.schedule}</p>
              <p className="text-blue-700">{route.cost}</p>
              <p className="text-blue-600 text-xs">{route.walkOn} (walk-on)</p>
            </div>
          </div>
        ))}

        <div className="border-t border-blue-200 pt-3 space-y-2">
          <p className="text-xs text-blue-800">{ferry.ferryNote}</p>
          <p className="text-xs text-blue-600 italic">Prices vary by season — check BC Ferries for current fares before you go.</p>
          <a
            href={ferry.bcFerriesUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            BC Ferries schedules &amp; fares
          </a>
          <p className="text-xs text-blue-500">Tap above to get current schedules and fares</p>
        </div>
      </div>

      {/* Wineries */}
      <div>
        <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wide mb-3">
          Wineries on {ferry.island}
        </h3>

        {loading && (
          <div className="text-center py-10 text-stone-400 text-sm">Loading wineries…</div>
        )}

        {!loading && wineries.length === 0 && (
          <div className="text-center py-10 text-stone-400 text-sm">
            No wineries listed for this island yet.
          </div>
        )}

        <div className="space-y-4">
          {wineries.map(winery => (
            <WineryCard key={winery.id} winery={winery} />
          ))}
        </div>
      </div>
    </div>
  );
}
